'use client';

import { motion } from 'framer-motion';
import { useState, useCallback, type ReactNode } from 'react';

interface FadeInProps {
  children: ReactNode;
  className?: string;
  /** Delay in seconds — applies only if element is in viewport on mount */
  delay?: number;
  /** Duration in seconds */
  duration?: number;
}

/**
 * Standalone fade-in element. Triggers when it enters the viewport.
 * `delay` only applies to elements visible on initial load (for stagger effect).
 * Elements scrolled into view later appear immediately.
 * Also works as a child of FadeInGroup (uses parent stagger instead).
 */
export function FadeIn({ children, className, delay = 0, duration = 0.4 }: FadeInProps) {
  const [actualDelay, setActualDelay] = useState(delay);

  // Callback ref: check viewport on mount to decide if delay applies
  const callbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || delay === 0) return;
      const rect = node.getBoundingClientRect();
      const inViewport = rect.top < window.innerHeight && rect.bottom > 0;
      if (!inViewport) {
        setActualDelay(0);
      }
    },
    [delay]
  );

  return (
    <motion.div
      ref={callbackRef}
      className={className}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration, delay: actualDelay }}
      variants={{
        hidden: { opacity: 0, y: 16 },
        visible: { opacity: 1, y: 0, transition: { duration } },
      }}
    >
      {children}
    </motion.div>
  );
}

interface FadeInGroupProps {
  children: ReactNode;
  className?: string;
  /** Delay between each child in seconds */
  stagger?: number;
}

/**
 * Wrapper that staggers the appearance of FadeIn children.
 * Overrides individual whileInView — animation is controlled by the group.
 */
export function FadeInGroup({ children, className, stagger = 0.1 }: FadeInGroupProps) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-50px' }}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: stagger } },
      }}
    >
      {children}
    </motion.div>
  );
}
